import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Dfns from 'date-fns/fp';

import { MdOutlineFileDownload } from 'react-icons/md';

import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { download } from '@src/utils/download-utils';
import { useMap } from '@src/utils/hooks/useData';
import { isNotMonday } from '@src/utils/date-utils';

import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { EmptyState } from '@Components/EmptyState';
import { Img } from '@Components/Img';
import { Dropdown } from '@Components/Dropdown';
import { DatePicker } from '@Components/DatePicker';
import { IconButton } from '@Components/IconButton';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { ChartContainer } from '@Components/ChartContainer';

import { ChartsContainer } from './ChartsContainer';
import { MetricsTableContainer } from './MetricsTableContainer';
import {
  CUTOFF_DATE,
  DEFAULT_DATE,
  EXPAND_KEYS,
  EXPAND_LABELS,
  ExpandByKey,
  ExpandByMap,
  TABLE_METRIC_OPTIONS,
  TableMetric
} from './competitive-metrics-constants';
import { useCompetitiveMetricsData } from './use-competitive-metrics-data';

import './CompetitiveMetrics.scss';

interface CompetitiveMetricsBodyProps {
  hasNoCompetitors: boolean;
  data: ReturnType<typeof useCompetitiveMetricsData>['data'];
  error: Error | null;
  expandByMap: ExpandByMap;
  setExpandByValue: (key: ExpandByKey, value: boolean) => void;
  setCsv: (csv: string) => void;
  downloadCsv: () => void;
  validDays: string[];
}

const CompetitiveMetricsBody = ({
  hasNoCompetitors,
  data,
  error,
  expandByMap,
  setExpandByValue,
  setCsv,
  downloadCsv,
  validDays
}: CompetitiveMetricsBodyProps) => {
  if (hasNoCompetitors) {
    return <EmptyState title='No competitors configured' />;
  }
  if (error) {
    return <EmptyState title="Couldn't load competitor data" subtitle={error.message} />;
  }
  if (!data) {
    return <Spinner />;
  }
  return (
    <>
      <ChartsContainer
        creativesRawData={data.creativesRawData}
        costPersRawData={data.costPersRawData}
      />
      <ChartContainer
        className='metrics-table-card'
        title='Network & Daypart Breakdown'
        viewRightActions
        rightActions={
          <MetricsTableActions
            expandByMap={expandByMap}
            setExpandByValue={setExpandByValue}
            downloadCsv={downloadCsv}
          />
        }
      >
        <MetricsTableContainer
          costPersRawData={data.costPersRawData}
          weekNames={validDays}
          expandBy={expandByMap}
          setCsv={setCsv}
        />
      </ChartContainer>
    </>
  );
};

export const CompetitiveMetrics = () => {
  const companyInfo = useCompanyInfo();

  const [selectedCompetitor, setSelectedCompetitor] = useState<string>();
  const [csv, setCsv] = useState('');
  const [weeks, setWeeks] = useState({
    start: DEFAULT_DATE,
    end: DEFAULT_DATE
  });

  const [expandByMap, setExpandByValue] = useMap<ExpandByKey, boolean>({
    length: false,
    network: true,
    dayPart: true,
    spots: false
  });

  useEffect(() => {
    if (companyInfo && companyInfo.competitors) {
      setSelectedCompetitor(companyInfo.default_competitor || companyInfo.competitors[0]);
    }
  }, [companyInfo]);

  const downloadCsv = useCallback(() => {
    download(csv, `${selectedCompetitor} ${weeks.start}-${weeks.end}.csv`, 'text/csv');
  }, [csv, selectedCompetitor, weeks]);

  const { data, isLoading, error } = useCompetitiveMetricsData(selectedCompetitor);

  const validDays = useMemo(() => {
    if (!weeks) {
      return [];
    }
    if (weeks.start === weeks.end) {
      return [weeks.start];
    }
    return Dfns.eachDayOfInterval({
      start: Dfns.parseISO(weeks.start),
      end: Dfns.parseISO(weeks.end)
    }).map(Dfns.format('yyyy-MM-dd'));
  }, [weeks]);

  return (
    <Page
      title='Competitive Metrics'
      pageType='Competitive Metrics'
      className='competitive-metrics-page'
      minHeight='600px'
      isLoading={isLoading}
      actions={
        <div className='competitive-metrics-actions'>
          {selectedCompetitor && (
            <Img
              src={`https://cdn.blisspointmedia.com/competitors/${selectedCompetitor}.png`}
              className='company-logo'
              title={selectedCompetitor}
              unloader={
                <div className='logo-404'>
                  <span>{selectedCompetitor}</span>
                </div>
              }
            />
          )}
          <div className='competitor-select'>
            {selectedCompetitor &&
              companyInfo.competitors &&
              companyInfo.competitors.length > 1 && (
                <Dropdown
                  ariaLabel='Competitor'
                  value={selectedCompetitor}
                  options={companyInfo.competitors}
                  onChange={setSelectedCompetitor}
                />
              )}
          </div>

          <DatePicker
            range={weeks}
            fullWeeksOnly
            isOutsideRange={date => date > CUTOFF_DATE}
            isDayBlocked={isNotMonday}
            onChange={({ start, end }) => {
              if (start && end) {
                setWeeks({ start, end });
              }
            }}
          />
        </div>
      }
    >
      <div className='competitive-metrics-body'>
        <CompetitiveMetricsBody
          hasNoCompetitors={companyInfo.competitors?.length === 0}
          data={data}
          error={error}
          expandByMap={expandByMap}
          setExpandByValue={setExpandByValue}
          setCsv={setCsv}
          downloadCsv={downloadCsv}
          validDays={validDays}
        />
      </div>
    </Page>
  );
};

interface MetricsTableActionsProps {
  expandByMap: ExpandByMap;
  setExpandByValue: (key: ExpandByKey, value: boolean) => void;
  downloadCsv: () => void;
}

const MetricsTableActions = ({
  expandByMap,
  setExpandByValue,
  downloadCsv
}: MetricsTableActionsProps) => (
  <>
    <SegmentedControlButton
      options={TABLE_METRIC_OPTIONS}
      selectedOption={expandByMap.spots ? TableMetric.SPOTS : TableMetric.SPEND}
      onChange={metric => setExpandByValue('spots', metric === TableMetric.SPOTS)}
    />
    {EXPAND_KEYS.map(key => (
      <ToggleSwitch
        key={key}
        data-testid={`expand-${key}`}
        label={EXPAND_LABELS[key]}
        checked={Boolean(expandByMap[key])}
        onChange={() => setExpandByValue(key, !expandByMap[key])}
      />
    ))}
    <IconButton
      data-testid='download-csv-button'
      tooltip={{ text: 'Download CSV' }}
      onClick={downloadCsv}
    >
      <MdOutlineFileDownload />
    </IconButton>
  </>
);
